import { SubscriptionPaywallEnum } from "@nlc/shared-types";
import { useIsMutating } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { UpsellModal } from "@/components/upgrade-modals/upsell-modal";
import FaceFrownIcon from "@/features/subscription/components/face-frown.svg?react";
import { useUser } from "@/hooks/useUser";
import { isKickstarterAccount } from "@/utils/isKickstarterAccount";
import { isMaintenancePlan } from "@/utils/isMaintenancePlan";
import { isPausePlan } from "@/utils/isPausePlan";

export function Footer() {
  const isMutating = useIsMutating();
  const { currentProject, data: userData } = useUser();
  const navigate = useNavigate();
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);

  const isKickstarter = isKickstarterAccount(
    userData?.user.accountType ?? null,
  );

  const isPause = isPausePlan(currentProject?.accountType ?? null);
  const isFirstDraft =
    currentProject?.isFirstDraft &&
    !currentProject.subscriptionStatus.subscriptionActive;
  const isMaintenanceOutOfCredits =
    isMaintenancePlan(currentProject?.product) &&
    currentProject?.subscriptionStatus.paywallType ===
      SubscriptionPaywallEnum.NO_CREDITS;
  const isIntercepted =
    isKickstarter || isPause || isFirstDraft || isMaintenanceOutOfCredits;

  const handleSubmitClick = () => {
    if (isPause || isFirstDraft) {
      setIsReactivateModalOpen(true);
    } else if (isKickstarter || isMaintenanceOutOfCredits) {
      setIsUpsellModalOpen(true);
    }
  };

  const handleUpgrade = () => {
    setIsUpsellModalOpen(false);
    navigate({ to: "/pricing" });
  };

  return (
    <>
      <div className="sticky bottom-0 flex items-center justify-end gap-lg rounded-b-xl border-t border-border-secondary bg-bg-primary px-3xl py-2xl">
        <Button
          isDisabled={isMutating > 0}
          onPress={isIntercepted ? handleSubmitClick : undefined}
          size="md"
          type={isIntercepted ? "button" : "submit"}
          variant="primary"
        >
          Create request
        </Button>
      </div>

      <UpsellModal
        isOpen={isUpsellModalOpen}
        onOpenChange={setIsUpsellModalOpen}
        onUpgrade={handleUpgrade}
        type="draft"
      />

      <Modal
        classNames={{ modal: "w-[400px]" }}
        isDismissable
        isOpen={isReactivateModalOpen}
        onOpenChange={setIsReactivateModalOpen}
      >
        <Dialog>
          <DialogBody className="p-6">
            <DialogCloseButton />
            <div className="flex flex-col items-center gap-3xl mt-6">
              <FaceFrownIcon />
              <p className="text-center text-lg font-semibold text-text-primary">
                Whoops! You&apos;re out of design credits.
                <br />
                Please upgrade your plan or contact support for assistance.
              </p>
            </div>
          </DialogBody>
          <DialogFooter className="px-6 pb-6 pt-0">
            <div className="flex flex-col gap-sm w-full">
              <Button asChild className="w-full" size="lg" variant="primary">
                <a
                  href="https://nlc.com/manage-my-account/"
                  rel="noreferrer"
                  target="_blank"
                >
                  Upgrade now for unlimited creativity
                </a>
              </Button>
              <Button
                className="w-full"
                color="gray"
                onPress={() => setIsReactivateModalOpen(false)}
                size="lg"
                variant="secondary"
              >
                Maybe later
              </Button>
            </div>
          </DialogFooter>
        </Dialog>
      </Modal>
    </>
  );
}
